<template>
  <div class="live-page">
    <div class="live-shell">
      <header class="live-head">
        <button class="live-head-brand" type="button" @click="$router.replace('/Home')">
          {{ brandName }}
        </button>
        <div class="live-head-copy">
          <h1>{{ liveTitle }}</h1>
        </div>
        <span class="live-head-state">
          <i></i>
          <span>{{ copy.liveNow }}</span>
        </span>
      </header>

      <div class="live-slider">
        <button
          v-if="currentHero"
          :key="`hero-${currentHero.id}-${currentHeroIndex}`"
          class="live-slide"
          type="button"
          @click="openRoom(currentHero)"
        >
          <img v-if="currentHero.image" :src="currentHero.image" :alt="currentHero.title" />
          <div class="live-slide-copy">
            <span class="live-badge" :class="{ ai: currentHero.kind === 'ai' }">{{ currentHero.badge }}</span>
            <h2>{{ currentHero.title }}</h2>
            <p>{{ currentHero.subtitle }}</p>
          </div>
          <span class="live-enter">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 7l8 5-8 5V7z"></path>
            </svg>
          </span>
        </button>
        <div v-if="heroLives.length > 1" class="live-dots">
          <i
            v-for="(item, index) in heroLives"
            :key="`dot-${item.id}-${index}`"
            :class="{ on: index === currentHeroIndex }"
          ></i>
        </div>
      </div>

      <div class="live-chips">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="live-chip"
          :class="{ on: activeTab === tab.key }"
          type="button"
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
        </button>
      </div>

      <div class="live-sec">
        <div>
          <h2>{{ copy.sectionTitle }}</h2>
        </div>
        <span class="live-more">{{ copy.sectionTag }}</span>
      </div>

      <div class="live-grid">
        <button
          v-for="item in filteredLives"
          :key="item.id"
          class="live-card"
          :class="{ ai: item.kind === 'ai' }"
          type="button"
          @click="openRoom(item)"
        >
          <div class="live-cover">
            <img v-if="item.image" :src="item.image" :alt="item.title" />
            <div class="live-top">
              <span class="live-badge" :class="{ ai: item.kind === 'ai' }">{{ item.badge }}</span>
              <span v-if="item.kind !== 'ai'" class="live-viewers">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <circle cx="12" cy="8" r="3"></circle>
                  <path d="M5 20c.7-3.8 3-5.8 7-5.8s6.3 2 7 5.8"></path>
                </svg>
                {{ item.viewerText }}
              </span>
            </div>
            <span v-if="item.kind === 'ai'" class="live-state">{{ copy.liveOnAir }}</span>
            <div class="live-title">{{ item.title }}</div>
          </div>

          <div class="live-info">
            <span>{{ item.subtitle }}</span>
            <b>{{ item.cta }}</b>
          </div>
        </button>
      </div>

      <button
        v-if="hasMore"
        type="button"
        class="live-load-more"
        :disabled="loading"
        @click="loadMore"
      >
        {{ loading ? (copy.loadingText || "加载中…") : (copy.loadMore || "显示更多") }}
      </button>

      <div v-if="!filteredLives.length && !loading" class="live-empty">
        <b>{{ copy.emptyTitle }}</b>
        <span>{{ copy.emptyDesc }}</span>
      </div>
    </div>

    <Footer :is_show="true" />
  </div>
</template>

<script>
import { getListPayload } from "@/common/apiData";
import { getPageCopy } from "@/common/pageLocale";
import { randomOnlineCount, resolveOnlineCapText } from "@/common/onlineCount";
import Footer from "@/common/Footer.vue";

const PAGE_SIZE = 10;
/** 接口未配 online_min/max 时的默认跳动区间 */
const DEFAULT_ONLINE_MIN = 80;
const DEFAULT_ONLINE_MAX = 320;

export default {
  components: {
    Footer,
  },
  data() {
    return {
      activeTab: "recommend",
      liveRooms: [],
      page: 1,
      hasMore: false,
      loading: false,
      currentHeroIndex: 0,
      heroTimer: null,
      viewerTimer: null,
    };
  },
  computed: {
    copy() {
      return getPageCopy(this, "live");
    },
    baseInfo() {
      return this.$store.getters.getBaseInfo || {};
    },
    ui() {
      return this.baseInfo.ui || {};
    },
    brandName() {
      return String(this.ui.brand || this.baseInfo.name || "OMN")
        .replace(/MShub/gi, "OMN")
        .trim() || "OMN";
    },
    liveTitle() {
      const raw = this.ui.nav_live || this.copy.plazaTitle || "OMN直播间";
      return String(raw)
        .replace(/MShub/gi, "OMN")
        .replace(/直播广场/g, "直播间")
        .trim() || "OMN直播间";
    },
    tabs() {
      return [
        { key: "recommend", label: this.copy.recommend },
        { key: "nearby", label: this.copy.nearby },
        { key: "newcomer", label: this.copy.newcomer },
        { key: "hot", label: this.copy.hot },
      ];
    },
    aiRoom() {
      const seed = this.liveRooms[this.liveRooms.length - 1] || this.liveRooms[0] || {};
      return {
        id: `ai-${seed.id || 1}`,
        sourceId: seed.id || "",
        title: this.copy.aiTitle,
        subtitle: this.copy.aiSubtitle,
        image: seed.image || "",
        viewerText: "∞",
        badge: this.copy.aiBadge,
        kind: "ai",
        cta: this.copy.enterAiRoom,
        isNew: true,
        isHot: true,
        isNearby: true,
      };
    },
    regularRooms() {
      if (!this.liveRooms.length) {
        return [];
      }
      const aiSourceId = this.aiRoom.sourceId;
      return this.liveRooms.filter((item) => item.sourceId !== aiSourceId);
    },
    allRooms() {
      return [this.aiRoom].concat(this.regularRooms);
    },
    heroLives() {
      return this.allRooms.slice(0, 3);
    },
    currentHero() {
      return this.heroLives[this.currentHeroIndex] || this.heroLives[0] || null;
    },
    filteredLives() {
      let rooms = this.allRooms;
      if (this.activeTab === "nearby") {
        rooms = rooms.filter((item) => item.isNearby);
      } else if (this.activeTab === "newcomer") {
        rooms = rooms.filter((item) => item.isNew);
      } else if (this.activeTab === "hot") {
        rooms = rooms.filter((item) => item.isHot);
      }
      return rooms.length ? rooms : this.allRooms;
    },
  },
  created() {
    this.fetchLives(true);
  },
  beforeDestroy() {
    this.stopHeroRotation();
    this.stopViewerJitter();
  },
  methods: {
    parseOnlineBound(value) {
      if (value === "" || value == null) {
        return null;
      }
      const n = Number(value);
      if (!Number.isFinite(n) || n < 0) {
        return null;
      }
      return Math.floor(n);
    },
    roomOnlineSource(room) {
      const item = room || {};
      const raw = item.raw || {};
      return {
        online_min: item.online_min ?? item.onlineMin ?? raw.online_min,
        online_max: item.online_max ?? item.onlineMax ?? raw.online_max,
      };
    },
    pickViewerText(room) {
      // 优先用接口区间；0/0 或未配置时用默认区间随机，避免一直显示 0
      const fromItem = randomOnlineCount(this.roomOnlineSource(room));
      if (fromItem && fromItem !== "0") {
        return fromItem;
      }
      const fromUi = randomOnlineCount(this.ui, DEFAULT_ONLINE_MIN, DEFAULT_ONLINE_MAX);
      if (fromUi && fromUi !== "0") {
        return fromUi;
      }
      const fallback = randomOnlineCount({}, DEFAULT_ONLINE_MIN, DEFAULT_ONLINE_MAX);
      return fallback || resolveOnlineCapText(this.ui, this.copy.onlineCountCap || "99+");
    },
    normalizeRoom(item, index) {
      const title = String(item.xuanfei_name || item.name || "").trim();
      const image = item.img_url || item.image || "";
      const feeStar = Number(item.fee_star);
      let onlineMin = this.parseOnlineBound(item.online_min);
      let onlineMax = this.parseOnlineBound(item.online_max);
      if (onlineMin != null && onlineMax != null && onlineMin <= 0 && onlineMax <= 0) {
        onlineMin = null;
        onlineMax = null;
      }
      const room = {
        id: item.id || index + 1,
        sourceId: item.id || index + 1,
        title: title || this.copy.liveBadge || "LIVE",
        subtitle: this.copy.roomSubtitle,
        image,
        onlineMin,
        onlineMax,
        feeStar: Number.isFinite(feeStar) ? Math.max(0, Math.min(5, Math.floor(feeStar))) : 0,
        badge: this.copy.liveBadge,
        kind: "locked",
        cta: this.copy.enterRoom,
        isNew: index < 3,
        isHot: index < 6 || Number(item.is_hot) === 1,
        isNearby: index % 2 === 0 || Number(item.is_ranking) === 1,
        raw: item,
      };
      room.viewerText = this.pickViewerText(room);
      return room;
    },
    loadMore() {
      if (this.loading || !this.hasMore) {
        return;
      }
      this.fetchLives(false);
    },
    async fetchLives(reset) {
      if (this.loading) {
        return;
      }
      const nextPage = reset ? 1 : this.page + 1;
      this.loading = true;
      try {
        const res = await this.$http({
          method: "get",
          url: "xuanfeilist",
          data: { page: nextPage, limit: PAGE_SIZE },
        });
        const rows = getListPayload(res && res.data);
        const offset = reset ? 0 : this.liveRooms.length;
        const mapped = rows.map((item, index) => this.normalizeRoom(item, offset + index));
        this.liveRooms = reset ? mapped : this.liveRooms.concat(mapped);
        this.page = nextPage;
        this.hasMore = rows.length >= PAGE_SIZE;
        this.resetHeroRotation();
        this.startViewerJitter();
      } catch (error) {
        if (reset) {
          this.liveRooms = [];
          this.hasMore = false;
          this.stopHeroRotation();
          this.stopViewerJitter();
        }
      } finally {
        this.loading = false;
      }
    },
    startViewerJitter() {
      this.stopViewerJitter();
      if (!this.liveRooms.length) {
        return;
      }
      this.viewerTimer = setInterval(() => {
        this.liveRooms = this.liveRooms.map((room) => ({
          ...room,
          viewerText: this.pickViewerText(room),
        }));
      }, 4500);
    },
    stopViewerJitter() {
      if (this.viewerTimer) {
        clearInterval(this.viewerTimer);
        this.viewerTimer = null;
      }
    },
    resetHeroRotation() {
      if (!this.heroLives.length) {
        this.currentHeroIndex = 0;
        this.stopHeroRotation();
        return;
      }
      this.currentHeroIndex = Math.floor(Math.random() * this.heroLives.length);
      this.startHeroRotation();
    },
    startHeroRotation() {
      this.stopHeroRotation();
      if (this.heroLives.length <= 1) {
        return;
      }
      this.heroTimer = setInterval(() => {
        let nextIndex = this.currentHeroIndex;
        while (nextIndex === this.currentHeroIndex && this.heroLives.length > 1) {
          nextIndex = Math.floor(Math.random() * this.heroLives.length);
        }
        this.currentHeroIndex = nextIndex;
      }, 3200);
    },
    stopHeroRotation() {
      if (this.heroTimer) {
        clearInterval(this.heroTimer);
        this.heroTimer = null;
      }
    },
    openRoom(item) {
      this.$router.push({
        path: "/LiveRoom",
        query: {
          mode: item.kind,
          id: item.sourceId || item.id,
          title: item.title,
          subtitle: item.subtitle,
          image: item.image,
          viewers: item.viewerText,
          online_min: item.onlineMin != null ? item.onlineMin : "",
          online_max: item.onlineMax != null ? item.onlineMax : "",
          fee_star: item.feeStar != null ? item.feeStar : "",
          badge: item.badge,
        },
      });
    },
  },
};
</script>

<style lang="less" scoped>
.live-page {
  min-height: 100vh;
  min-height: 100dvh;
  background:
    radial-gradient(circle at 50% 0, rgba(165, 92, 255, 0.14), transparent 30%),
    radial-gradient(circle at 100% 12%, rgba(255, 63, 120, 0.12), transparent 26%),
    #09060f;
  color: #fff;
}

.live-shell {
  width: min(100%, 23.4375rem);
  margin: 0 auto;
  padding: max(0.75rem, env(safe-area-inset-top)) 0.75rem 6.8rem;
}

.live-head {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.15rem 0 1rem;
}

.live-head-brand {
  border: 0;
  padding: 0;
  background: transparent;
  color: #fff;
  font-size: 1.08rem;
  font-weight: 900;
  line-height: 1;
}

.live-head-brand em {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.15rem;
  height: 1.45rem;
  margin-left: 0.12rem;
  border-radius: 0.42rem;
  background: linear-gradient(180deg, #ff5c9b, #ff3f78);
  color: #180612;
  font-style: normal;
}

.live-head-copy h1 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 900;
}

.live-head-copy p {
  margin: 0.16rem 0 0;
  color: rgba(255, 255, 255, 0.58);
  font-size: 0.72rem;
}

.live-head-state {
  display: inline-flex;
  align-items: center;
  gap: 0.38rem;
  min-height: 2.1rem;
  padding: 0 0.78rem;
  border: 1px solid rgba(255, 95, 151, 0.3);
  border-radius: 999px;
  background: rgba(28, 15, 35, 0.72);
  color: #ff7dad;
  font-size: 0.72rem;
  font-weight: 800;
}

.live-head-state i {
  width: 0.42rem;
  height: 0.42rem;
  border-radius: 50%;
  background: #ff4b85;
  box-shadow: 0 0 0.5rem rgba(255, 75, 133, 0.55);
}

.live-slider {
  position: relative;
  padding-bottom: 1.1rem;
}

.live-slide {
  position: relative;
  display: block;
  width: 100%;
  height: 12rem;
  border: 0;
  border-radius: 1.45rem;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
}

.live-slide img,
.live-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.live-slide::after,
.live-cover::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(10, 6, 15, 0.08) 0%, rgba(10, 6, 15, 0.18) 36%, rgba(10, 6, 15, 0.84) 100%);
}

.live-slide-copy,
.live-enter,
.live-top,
.live-state,
.live-title {
  position: absolute;
  z-index: 1;
}

.live-slide-copy {
  left: 0.95rem;
  right: 4rem;
  bottom: 1rem;
  text-align: left;
}

.live-slide-copy h2 {
  margin: 0.55rem 0 0;
  font-size: 1.9rem;
  font-weight: 900;
}

.live-slide-copy p {
  margin: 0.2rem 0 0;
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.84rem;
  font-weight: 700;
}

.live-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.9rem;
  height: 1.45rem;
  padding: 0 0.56rem;
  border-radius: 999px;
  background: linear-gradient(135deg, #ff4b85, #ff6b68);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 900;
  line-height: 1;
  white-space: nowrap;
}

.live-badge.ai {
  background: linear-gradient(135deg, #bd63ff, #ff4f8c);
}

.live-enter {
  right: 0.95rem;
  bottom: 1.02rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.9rem;
  height: 2.9rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff5896, #ff3977);
  color: #fff;
}

.live-enter svg {
  width: 1.25rem;
  height: 1.25rem;
}

.live-dots {
  position: absolute;
  left: 50%;
  bottom: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  transform: translateX(-50%);
}

.live-dots i {
  width: 0.34rem;
  height: 0.34rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.26);
}

.live-dots i.on {
  width: 1rem;
  border-radius: 999px;
  background: linear-gradient(90deg, #bd63ff, #ff4f8c);
}

.live-chips {
  display: flex;
  gap: 0.62rem;
  margin-top: 1rem;
  overflow-x: auto;
  padding-bottom: 0.2rem;
  scrollbar-width: none;
}

.live-chips::-webkit-scrollbar {
  display: none;
}

.live-chip {
  flex: 0 0 auto;
  min-width: 3.1rem;
  height: 2.1rem;
  padding: 0 0.88rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  background: rgba(20, 12, 28, 0.86);
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.82rem;
  font-weight: 800;
}

.live-chip.on {
  border-color: rgba(255, 102, 160, 0.44);
  box-shadow: inset 0 0 0 1px rgba(255, 85, 148, 0.18);
  color: #fff;
}

.live-sec {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
}

.live-sec h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 900;
}

.live-sec .sub {
  margin: 0.18rem 0 0;
  color: rgba(255, 255, 255, 0.76);
  font-size: 0.82rem;
}

.live-more {
  color: #ff6ea2;
  font-size: 0.82rem;
  font-weight: 800;
}

.live-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.88rem;
  margin-top: 0.92rem;
}

.live-card {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1.15rem;
  overflow: hidden;
  background: rgba(19, 10, 28, 0.88);
  color: #fff;
  text-align: left;
}

.live-cover {
  position: relative;
  height: 14rem;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
}

.live-top {
  top: 0.7rem;
  left: 0.6rem;
  right: 0.6rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.45rem;
}

.live-viewers {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  min-height: 1.42rem;
  padding: 0 0.48rem;
  border-radius: 999px;
  background: rgba(9, 7, 14, 0.7);
  color: #fff;
  font-size: 0.66rem;
  font-weight: 800;
}

.live-viewers svg {
  width: 0.8rem;
  height: 0.8rem;
}

.live-state {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  top: 0.7rem;
  right: 0.6rem;
  min-height: 1.45rem;
  padding: 0 0.56rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.9);
  color: #291227;
  font-size: 0.68rem;
  font-weight: 900;
  line-height: 1;
  white-space: nowrap;
}

.live-title {
  left: 0.72rem;
  right: 0.72rem;
  bottom: 0.72rem;
  font-size: 1.1rem;
  font-weight: 900;
}

.live-load-more {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 2.75rem;
  margin-top: 0.95rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  color: #ff7eae;
  font-size: 0.88rem;
  font-weight: 800;
}

.live-load-more[disabled] {
  opacity: 0.55;
}

.live-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.55rem;
  min-height: 3rem;
  padding: 0 0.72rem;
  font-size: 0.7rem;
}

.live-info span {
  color: rgba(255, 255, 255, 0.78);
}

.live-info b {
  color: #ff73a8;
  font-weight: 900;
  white-space: nowrap;
}

.live-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.45rem;
  padding: 2rem 0 0;
  color: rgba(255, 255, 255, 0.82);
}

.live-empty b {
  font-size: 1.05rem;
}

@media (min-width: 48rem) {
  .live-shell {
    width: 23.4375rem;
  }
}
</style>
